import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { Control, ControllerRenderProps, FieldError } from 'react-hook-form';

import { slugify } from '../../src/utils/slugify.ts';
import { FieldOption } from '../types/FormField.ts';
import { OptionalTextField } from './OptionalTextField.tsx';
import type { SxProps } from '@mui/system';

export interface RadioFieldProps {
  label: string;
  disabled: boolean;
  value?: string;
  onChange: ControllerRenderProps['onChange'];
  error?: FieldError;
  name: string;
  readOnly?: boolean;
  options: FieldOption[];
  direction?: 'horizontal' | 'vertical';
  control: Control;
  itemWrapperProps?: {
    sx?: SxProps;
    [key: string]: unknown;
  };
}

export const RadioField = ({
  label,
  disabled,
  value,
  onChange,
  error,
  name,
  readOnly = false,
  options,
  direction = 'horizontal',
  control,
  itemWrapperProps = {},
}: RadioFieldProps) => {
  return (
    <>
      <FormControl sx={{ pointerEvents: readOnly ? 'none' : 'auto' }}>
        <FormLabel id={`${name}-label`} disabled={disabled}>
          {label}
        </FormLabel>

        <RadioGroup aria-labelledby={`${name}-label`} value={value} onChange={onChange}>
          <Stack direction={direction === 'horizontal' ? 'row' : 'column'} flexWrap="wrap" columnGap={3} rowGap={1}>
            {options.map((option, i) => {
              const valueSlug = slugify(option.value);
              const { sx = {}, ...otherProps } = itemWrapperProps;

              return (
                <Box key={`${name}-radio-${valueSlug}${i}`} sx={{ display: 'flex', alignItems: 'flex-start', ...sx }} {...otherProps}>
                  <FormControlLabel
                    key={`${name}-radio-${valueSlug}`}
                    disabled={disabled}
                    value={option.value}
                    control={<Radio />}
                    checked={value === option.value}
                    label={option.label || option.value}
                  />
                  {option?.textField && (
                    <OptionalTextField
                      dependsOn={{
                        name: name,
                        disabled: Boolean(disabled || readOnly),
                        value: option.value,
                      }}
                      control={control}
                      textField={option.textField}
                      key={`${name}-${valueSlug}-additional`}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </RadioGroup>
      </FormControl>
      {error && (
        <Typography variant="caption" color="error">
          {error!.message as string}
        </Typography>
      )}
    </>
  );
};
